import axios from 'axios';
import { API_URL } from '../config';

// Types
export interface QuoteItem {
  id?: number;
  productId: number;
  productName: string;
  quantity: number;
  unitPrice: number;
  discount: number;
  taxRate: number;
  isCustom?: boolean; // Indique si c'est un article personnalisé (non-catalogué)
}

export interface Quote {
  id?: number;
  reference: string;
  customerId: number;
  customerName: string;
  issueDate: string;
  expiryDate: string;
  status: 'draft' | 'sent' | 'accepted' | 'rejected' | 'expired';
  subtotal: number;
  taxAmount: number;
  discountAmount: number;
  total: number;
  notes?: string;
  termsAndConditions?: string;
  objet?: string;
  rejection_reason?: string;
  items: QuoteItem[];
  createdAt?: string;
  updatedAt?: string;
  // Diagnostic workflow fields
  intervention_id?: number;
  diagnostic_report_id?: number;
  payment_status?: 'pending' | 'paid' | 'failed' | 'deferred';
  // Scheduled intervention fields
  scheduled_date?: string;
  execute_now?: boolean;
  second_contact?: string;
  // Split payment fields (50%)
  payment_type?: 'full' | 'split';
  first_payment_amount?: number;
  first_payment_status?: 'pending' | 'paid';
  second_payment_amount?: number;
  second_payment_status?: 'pending' | 'paid';
}

export type StatusType = Quote['status'];

export interface QuoteFilters {
  status?: string;
  customerId?: number;
  startDate?: string;
  endDate?: string;
  search?: string;
  page?: number;
  limit?: number;
}

// Configuration axios pour les appels API
const api = axios.create({
  baseURL: `${API_URL}/quotes`,
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${localStorage.getItem('token')}`
  }
});

// Intercepteur pour gérer l'authentification
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const quotesService = {
  // Récupérer tous les devis
  async getQuotes(filters: QuoteFilters = {}) {
    const response = await api.get('', { params: filters });
    return response.data;
  },

  // Récupérer un devis par son ID
  async getQuoteById(id: number) {
    const response = await api.get(`/${id}`);
    // Toujours retourner le contenu sous data.data pour garantir la structure
    return response.data.data || {};
  },

  // Créer un nouveau devis
  async createQuote(quoteData: Omit<Quote, 'id' | 'createdAt' | 'updatedAt'>) {
    const response = await api.post('', quoteData);
    return response.data;
  },

  // Mettre à jour un devis existant
  async updateQuote(id: number, quoteData: Partial<Quote>) {
    const response = await api.put(`/${id}`, quoteData);
    return response.data;
  },

  // Supprimer un devis
  async deleteQuote(id: number) {
    const response = await api.delete(`/${id}`);
    return response.data;
  },

  // Changer le statut d'un devis
  async updateQuoteStatus(id: number, status: Quote['status']) {
    const response = await api.patch(`/${id}/status`, { status });
    return response.data;
  },

  // Générer un PDF pour un devis
  async generatePdf(id: number) {
    const response = await api.get(`/${id}/pdf`, { responseType: 'blob' });
    return response.data;
  },

  // Envoyer un devis par email
  async sendByEmail(id: number, emailData: { to: string; subject?: string; message?: string }) {
    const response = await api.post(`/${id}/send-email`, emailData);
    return response.data;
  },

  // Convertir un devis en commande
  async convertToOrder(id: number, orderData: any) {
    const response = await api.post(`/${id}/convert-to-order`, orderData);
    return response.data.data; // Retourner directement la commande
  }
};

export default quotesService;